<template>
    <div class="responsive-image"
        :class="{ 
            'responsive-image--loading': isLoading, 
            'responsive-image--error': isError 
        }" 
        :style="containerStyle"
        ref="container"
        @mousedown="startDrag"
        @mouseup="stopDrag"
        @wheel="zoomImage"
    >
        <!-- Плейсхолдер для lazy режима -->
        <img 
            v-if="lazyLoading && !hasLoaded" 
            :src="lazySrc" 
            class="responsive-image__lazy" 
            :style="imgStyle" 
            alt="" 
        />

        <!-- Основное изображение -->
        <img 
            v-if="src && (!lazyLoading || isVisible)" 
            ref="image" 
            :src="src" 
            :alt="alt" class="responsive-image__img"
            :style="imgStyle" 
            @load="onLoad" 
            @error="onError" 
        />

        <!-- Заглушка при ошибке -->
        <slot v-if="isError" name="error">
            <div class="responsive-image__error">Ошибка загрузки</div>
        </slot>

        <!-- Прелоадер -->
        <v-progress-circular 
            v-if="isLoading" 
            indeterminate 
            color="grey"
            class="responsive-image__loader"
        ></v-progress-circular>
    </div>
</template>

<script>
export default {
    name: "ResponsiveImage",
    props: {
        src: String,
        lazySrc: String, // Плейсхолдер при lazy-loading
        alt: String,
        width: [String, Number],
        height: [String, Number],
        contain: Boolean,
        cover: Boolean,
        lazyLoading: Boolean, // Включить lazy загрузку
        aspectRatio: Number,
        transition: String,
        cropImage: Boolean,
        draggable: Boolean,
        maxScale: {
            type: Number,
            default: 10
        },
        minScale: {
            type: Number,
            default: 0
        },
        scale: Number,
        x: Number,
        y: Number,
    },
    data() {
        return {
            isLoading: true,
            isError: false,
            hasLoaded: false,
            isVisible: false, // Для lazy
            observer: null,
            xOffset: 0,
            yOffset: 0,
            imageScale: 1,
            isDragging: false,
            lastMouseX: 0,
            lastMouseY: 0,
            zoomFactor: 0.05,
            firstLoad: true,
            imageSize: {
                width: 0,
                height: 0
            }
        };
    },
    computed: {
        containerWidth() {
            return this.$refs?.container?.offsetWidth || 0;
        },
        containerHeight() {
            return this.$refs?.container?.offsetHeight || 0;
        },
        incomeContainerWidth() {
            return this.getContainerSide();
        },
        incomeContainerHeight() {
            return this.getContainerSide(false);
        },
        containerStyle() {
            return {
                width: this.incomeContainerWidth,
                height: this.incomeContainerHeight,
                position: "relative",
                overflow: "hidden",
                display: "flex",
                alignItems: this.cropImage ? "flex-start" : "center",
                justifyContent: this.cropImage ? "flex-start" : "center",
                cursor: this.draggable ? "grab" : "default",
            };
        },
        imgStyle() {
            return {
                objectFit: this.contain ? "contain" : this.cover ? "cover" : "unset",
                width: this.cropImage ? 'auto' : "100%",
                height: this.cropImage ? 'auto' : "100%",
                transition: (this.transition ? this.transition : "opacity 0.3s") +", transform  0.1s",
                opacity: this.isLoading ? 0 : 1,
                transform: `translateX(${this.xOffset}px) translateY(${this.yOffset}px) scale(${this.imageScale})`,
                transformOrigin: '0 0',
            };
        },
    },
    watch: {
        src: {
            immediate: true,
            handler(newSrc) {
                if (!newSrc) {
                    return;
                }
                this.isLoading = true;
                this.isError = false;    
                this.xOffset = 0;
                this.yOffset = 0;
                this.imageScale = 1;
                this.$emit("loadstart");
            },
        },
        xOffset(v) {
            this.$emit('update:x', v);
        },
        yOffset(v) {
            this.$emit('update:y', v);
        },
        imageScale(v) {
            this.$emit('update:scale', v);
        },
    },
    mounted() {
        if (this.lazyLoading) {
            this.initIntersectionObserver();
        }
        window.addEventListener("mousemove", this.dragImage);
        window.addEventListener("mouseup", this.stopDrag);
    },
    beforeDestroy() {
        if (this.observer) {
            this.observer.disconnect();
        }
        window.removeEventListener("mousemove", this.dragImage);
        window.removeEventListener("mouseup", this.stopDrag);
    },
    methods: {
        isNumber(v) {
            return v !== '' && this._.isFinite(+v);
        },
        getContainerSide(isWidth = true) {
            const s1 = isWidth ? this.width : this.height;
            if (this.isNumber(s1)) {
                return s1 + 'px';
            }
            if (s1) {
                return s1;
            }
            const s2 = isWidth ? this.height : this.width;
            if (s2 === 'auto') {
                return 'auto';
            }
            if (this.aspectRatio && (s2 || s2 === 0)) {
                if (this.isNumber(s2)) {
                    return isWidth ? +s2 * this.aspectRatio : +s2 / this.aspectRatio;
                }
                if (!s2) {
                    return '100%';
                }
                const m = s2.match(/[0-9]+/);
                if (this.isNumber(m[0])) {
                    return s2.replace(m[0], isWidth ? +m[0] * this.aspectRatio : +m[0] / this.aspectRatio);
                }
            }
            return "100%";
        },
        onLoad() {
            this.isLoading = false;
            this.hasLoaded = true;
            this.$emit("load");
            if (!this.cropImage) {
                return;
            }
            setTimeout(() => {
                const   img = this.$refs.image,
                        h = img?.offsetHeight || 0,
                        w = img?.offsetWidth || 0,
                        ch = this.containerHeight,
                        cw = this.containerWidth,
                        r = w / h,
                        cr = cw / ch,
                        z = r > cr ? ch / h : cw / w
                ;
                this.imageSize = {width: w, height: h};
                this.imageScale = this.firstLoad && this.scale !== undefined ? this.scale : z;
                this.xOffset = this.firstLoad && this.x !== undefined ? this.x : -(this.imageScale * w - cw) * 0.5;
                this.yOffset = this.firstLoad && this.y !== undefined ? this.y : -(this.imageScale * h - ch) * 0.5;
                this.firstLoad = false;
            }, 100);
        },
        onError() {
            this.isError = true;
            this.isLoading = false;
            this.$emit("error");
        },
        initIntersectionObserver() {
            this.observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        this.isVisible = true;
                        this.observer.disconnect();
                    }
                },
                { rootMargin: "0px", threshold: 0.1 }
            );
            this.observer.observe(this.$el);
        },
         // 🔹 Перемещение изображения мышкой
         startDrag(e) {
            if (!this.draggable) return;
            this.isDragging = true;
            this.lastMouseX = e.clientX;
            this.lastMouseY = e.clientY;
            e.preventDefault();
        },
        dragImage(e) {
            if (!this.isDragging) return;
            this.addToOffsets(e.clientX - this.lastMouseX, e.clientY - this.lastMouseY);
            this.lastMouseX = e.clientX;
            this.lastMouseY = e.clientY;
        },
        stopDrag() {
            this.isDragging = false;
        },
        // 🔹 Масштабирование изображения колесиком мыши
        zoomImage(e) {
            if (!this.draggable) return;
            e.preventDefault();
            const lastScale = this.imageScale;
            this.addToScale(e.deltaY < 0 ? this.zoomFactor : -this.zoomFactor);
            const scaleDiff = this.imageScale - lastScale;
            this.addToOffsets(-scaleDiff * this.imageSize.width * 0.5, -scaleDiff * this.imageSize.height * 0.5);
        },
        clampValue(value, min, max) {
            return Math.min(Math.max(value, min), max);
        },
        addToScale(delta) {
            this.imageScale = this.clampValue(this.imageScale + delta, Math.max(this.minScale, this.containerWidth / this.imageSize.width, this.containerHeight / this.imageSize.height), this.maxScale);
        },
        addToOffsets(xDelta, yDelta) {
            this.xOffset = this.clampValue(this.xOffset + xDelta, -Math.max(0, this.imageSize.width * this.imageScale - this.containerWidth), 0);
            this.yOffset = this.clampValue(this.yOffset + yDelta, -Math.max(0, this.imageSize.height * this.imageScale - this.containerHeight), 0);
        },
    },
};
</script>

<style scoped lang="scss">
.responsive-image {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;

    &__lazy {
        width: 100%;
        height: 100%;
        object-fit: cover;
        filter: blur(5px);
    }

    &__img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: opacity 0.3s;
        position: relative;
    }

    &__error {
        color: red;
        font-size: 14px;
        background: white;
        padding: 5px;
    }

    &__loader {
        position: absolute;
        transform: translate(-50%, -50%);
        top: 50%;
        left: 50%;
    }
}
</style>